import React, {
	type ReactNode,
	useMemo,
	useCallback,
	useEffect,
	type FunctionComponent,
} from 'react';
import { Field } from '@atlaskit/form';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import Select, { components, type OptionProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import type { ProjectType } from '@atlassian/jira-common-constants';
import { useIntl } from '@atlassian/jira-intl';

import { PROJECT_TYPE_DROPDOWN_ID } from '../../../common/constants';
import { getJiraProjects } from '../../../common/constants/source-items';
import type { CreateProjectConfig } from '../../../common/types';
import {
	useMappingState,
	useProjectData,
	useThirdPartyState,
} from '../../../controllers/import-state';
import messages from './messages';

export type Props = {
	projectSetting: CreateProjectConfig[];
};

export type OptionType = {
	value: ProjectType;
	label: ReactNode;
	description?: string;
	isRecommended?: boolean;
};

const CustomOption: FunctionComponent<OptionProps<OptionType>> = (props) => {
	const { label, description } = props.data;

	return (
		<components.Option {...props}>
			<Box>
				{label}
				{description && (
					<Box as="p" xcss={descriptionWrapperStyles}>
						{description}
					</Box>
				)}
			</Box>
		</components.Option>
	);
};

export const ProjectTypeSelector = ({ projectSetting }: Props) => {
	const { formatMessage } = useIntl();
	const [mappingState, { setMappingState }] = useMappingState();
	const [{ thirdPartyFieldConfigState }, { setThirdPartyState }] = useThirdPartyState();
	const [{ type }, { setProjectData }] = useProjectData();

	const options: OptionType[] = useMemo(() => {
		const jiraProjects = getJiraProjects();
		return projectSetting.map((item) => {
			const jiraProject = jiraProjects[item.productType];
			if (jiraProject) {
				return {
					value: item.productType,
					label: (
						<Box xcss={contentWrapperStyles}>
							{jiraProject.icon}
							<Box paddingInlineStart="space.100" xcss={titleWrapperStyles}>
								{formatMessage(jiraProject.name)}
								{item.isRecommended && (
									<Box as="span" paddingInlineStart="space.100">
										<Lozenge appearance="new">{formatMessage(messages.recommend)}</Lozenge>
									</Box>
								)}
							</Box>
						</Box>
					),
					description: jiraProject.description ? formatMessage(jiraProject.description) : undefined,
					isRecommended: item.isRecommended,
				};
			}
			return {
				value: item.productType,
				label: item.productType,
				isRecommended: item.isRecommended,
			};
		});
	}, [formatMessage, projectSetting]);

	const onSetProjectType = useCallback(
		(projectType: ProjectType) => {
			setProjectData({ type: projectType, template: null, style: null, accessLevel: null });
		},
		[setProjectData],
	);

	const defaultProjectType = useMemo(
		() => options.find(({ isRecommended }) => isRecommended) || options[0],
		[options],
	);

	const selectedProjectType = useMemo(
		() => options.find(({ value }) => value === type),
		[options, type],
	);

	useEffect(() => {
		if (!selectedProjectType && defaultProjectType) {
			onSetProjectType(defaultProjectType.value);
		}
	}, [defaultProjectType, onSetProjectType, selectedProjectType, type]);

	const onChange = useCallback(
		(option: OptionType | null) => {
			if (!option?.value) {
				return;
			}

			// Reset field mapping state if change the project type
			if (mappingState !== null) {
				setMappingState(null);
			}
			if (thirdPartyFieldConfigState !== null) {
				setThirdPartyState({
					thirdPartyFieldConfigState: null,
				});
			}

			onSetProjectType(option.value);
		},
		[
			mappingState,
			onSetProjectType,
			setMappingState,
			setThirdPartyState,
			thirdPartyFieldConfigState,
		],
	);

	return (
		<Field
			label={formatMessage(messages.label)}
			name={formatMessage(messages.label)}
			testId="project-create-importer-upload-flow.ui.project-create-screen.project-type-selector.field"
		>
			{({ fieldProps }) => (
				<Select<OptionType>
					{...fieldProps}
					inputId={PROJECT_TYPE_DROPDOWN_ID}
					name={PROJECT_TYPE_DROPDOWN_ID}
					options={options}
					value={selectedProjectType}
					onChange={onChange}
					aria-label={formatMessage(messages.label)}
					components={{
						Option: CustomOption,
					}}
				/>
			)}
		</Field>
	);
};

const contentWrapperStyles = xcss({
	display: 'flex',
	alignItems: 'center',
});

const titleWrapperStyles = xcss({
	font: token('font.body'),
});

const descriptionWrapperStyles = xcss({
	font: token('font.body.small'),
	marginTop: 'space.100',
	paddingLeft: 'space.400',
	marginBottom: 'space.100',
	color: 'color.text.subtlest',
});
